'use client';

import { ComponentProps, useState } from 'react';
import { useTrackedOrdersData } from '@/entities/order/lib/useTrackedOrdersData';
import { OrderProgress } from '@/entities/order/ui/order-progress';
import { OrderProgressList } from '@/entities/order/ui/order-progress-list';
import { cn } from '@/shared/lib/cn';
import { EventService } from '@/shared/lib/event-service';
import { Layout } from '@/shared/ui/layout';
import { Popover } from '@/shared/ui/popover';
import './tracked-orders-button.scss';

type Props = ComponentProps<typeof Layout> & {
    countBadgeProps: ComponentProps<typeof Layout>;
};

export const TrackedOrdersButton = ({ media, className, countBadgeProps, ...props }: Props) => {
    const [isOrdersOpen, setIsOrdersOpen] = useState(false);
    const { trackedOrders, firstOrderData, firstOrderState } = useTrackedOrdersData();

    if (!firstOrderData) {
        return null;
    }

    const handleOrderClick = (orderId: string) => {
        setIsOrdersOpen(false);
        EventService.trigger('order:view-alert-tracking', { orderId });
    };

    return (
        <Popover open={isOrdersOpen} onOpenChange={setIsOrdersOpen}>
            <Popover.Trigger asChild>
                <Layout
                    element="button"
                    type="button"
                    media={{ ...media, initial: { position: 'relative', ...media?.initial } }}
                    className={cn('tracked-orders-button', className)}
                    data-testid="order-progress-button"
                    {...props}
                >
                    <OrderProgress
                        as="div"
                        state={firstOrderState}
                        data-testid="order-progress-button-progress"
                    />
                    <Layout
                        element="span"
                        {...countBadgeProps}
                        className={cn('tracked-orders-button__badge', countBadgeProps.className)}
                    >
                        {trackedOrders.length}
                    </Layout>
                </Layout>
            </Popover.Trigger>
            <Popover.Content align="end" style={{ zIndex: 1000 }}>
                <OrderProgressList onOrderClick={handleOrderClick} />
            </Popover.Content>
        </Popover>
    );
};
